import React, { useMemo, useState } from "react";
import { Alert, Button, DatePicker, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import useLang from "../../../hooks/useLang";
import {
	DuplicateDeliveryPayment,
	DuplicateDeliveryPaymentRow,
	deleteDuplicatePayments,
	searchDuplicatePayments,
} from "../../../api/bulk-order-operations";
import {
	MAX_DUPLICATE_RANGE_DAYS,
	getCategoryConfig,
} from "../../../utils/bulkOperationsConfig";
import BulkOpsHeader from "../components/BulkOpsHeader";
import ConfirmModal from "../components/ConfirmModal";
import { PRIMARY_BUTTON_STYLE } from "../components/styles";

const { RangePicker } = DatePicker;

/**
 * The wire format is local wall-clock with NO timezone suffix. OrderPayment.CreatedAt is a plain
 * SQL `datetime` filled with GETDATE(), so an ISO "…Z" value would shift the window by the server
 * offset and silently return the wrong day.
 */
const toWireDate = (value: Dayjs) => value.format("YYYY-MM-DD");

const formatDateTime = (value: string) => {
	const date = dayjs(value);
	return date.isValid() ? date.format("DD/MM/YYYY HH:mm:ss") : value;
};

const formatMoney = (value: number) =>
	value.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

/** Distinct non-null transaction ids across an order's payment rows. */
const cashIds = (
	payments: DuplicateDeliveryPaymentRow[],
	pick: (row: DuplicateDeliveryPaymentRow) => number | null
) => Array.from(new Set(payments.map(pick).filter((id): id is number => !!id)));

const DuplicatePayments = () => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;
	const copy = strings?.duplicatePayments;
	const categoryStrings = strings?.categories?.duplicatePayments;

	const categoryConfig = getCategoryConfig("duplicate-payments");

	const [range, setRange] = useState<[Dayjs, Dayjs]>([
		dayjs().subtract(MAX_DUPLICATE_RANGE_DAYS - 1, "day"),
		dayjs(),
	]);
	const [loading, setLoading] = useState(false);
	const [searched, setSearched] = useState(false);
	const [orders, setOrders] = useState<DuplicateDeliveryPayment[]>([]);

	const [target, setTarget] = useState<DuplicateDeliveryPayment | null>(null);
	const [confirmWord, setConfirmWord] = useState("");
	const [deleting, setDeleting] = useState(false);

	// Inclusive day count: [Mon, Wed] is three calendar days and is allowed, so the gap may be
	// at most MAX_DUPLICATE_RANGE_DAYS - 1. Mirrors DuplicatePaymentService.
	const rangeDays =
		range[1].startOf("day").diff(range[0].startOf("day"), "day") + 1;
	const rangeTooWide = rangeDays > MAX_DUPLICATE_RANGE_DAYS;

	const totalOverpaid = useMemo(
		() => orders.reduce((sum, o) => sum + Math.abs(o.overpaidDelta), 0),
		[orders]
	);
	const deletableCount = useMemo(
		() => orders.filter((o) => o.canDelete).length,
		[orders]
	);

	// A result belongs to one window — leaving it on screen after the dates change invites
	// reading it as belonging to the new range.
	const changeRange = (value: [Dayjs, Dayjs]) => {
		setRange(value);
		setOrders([]);
		setSearched(false);
	};

	const search = async () => {
		if (rangeTooWide) return;

		setLoading(true);
		try {
			const result = await searchDuplicatePayments(
				toWireDate(range[0]),
				toWireDate(range[1])
			);
			setOrders(result.orders);
			setSearched(true);
		} catch {
			// The axios interceptor already toasts the API's message.
		} finally {
			setLoading(false);
		}
	};

	const closeConfirm = () => {
		setTarget(null);
		setConfirmWord("");
	};

	const confirmDelete = async () => {
		if (!target) return;

		setDeleting(true);
		try {
			const result = await deleteDuplicatePayments(target.orderId);
			toast.success(
				`${copy?.deleted ?? "Pagesat e dyfishuara u fshinë"}: ${
					result.deletedCount
				}`
			);
			closeConfirm();
			await search();
		} catch {
			// Interceptor toasts the refusal reason; keep the modal open so the row stays visible.
		} finally {
			setDeleting(false);
		}
	};

	const blockedReason = (row: DuplicateDeliveryPayment) => {
		if (!row.canDeleteBlockedReason) return undefined;
		return (
			copy?.blocked?.[row.canDeleteBlockedReason] ??
			copy?.blocked?.UNKNOWN ??
			"Kjo porosi nuk mund të korrigjohet automatikisht."
		);
	};

	const cashCell = (ids: number[]) =>
		ids.length === 0 ? (
			<Tag color="green">{copy?.notSettled ?? "Jo"}</Tag>
		) : (
			<span className="flex flex-wrap gap-1">
				{ids.map((id) => (
					<Tag color="red" key={id}>
						{id}
					</Tag>
				))}
			</span>
		);

	const columns = useMemo<ColumnsType<DuplicateDeliveryPayment>>(
		() => [
			{
				title: copy?.colOrder ?? "Porosia",
				dataIndex: "displayId",
				key: "displayId",
				render: (displayId: string | null, row) => (
					<span className="flex flex-col">
						<span className="font-medium">
							{displayId ?? `#${row.orderId}`}
						</span>
						<span className="text-xs text-gray-400">
							{row.orderId}
						</span>
					</span>
				),
			},
			{
				title: copy?.colDriverId ?? "ID e Shoferit",
				dataIndex: "deliveryDriverId",
				key: "deliveryDriverId",
				render: (driverId: number | null) => driverId ?? "—",
			},
			{
				title: copy?.colDriver ?? "Shoferi",
				key: "driver",
				render: (_: unknown, row) =>
					`${row.driverFirstName ?? ""} ${
						row.driverLastName ?? ""
					}`.trim() || "—",
			},
			{
				title: copy?.colPaymentDates ?? "Datat e pagesave",
				key: "payments",
				render: (_: unknown, row) => (
					<span className="flex flex-col gap-0.5">
						{row.payments.map((payment) => (
							<span key={payment.paymentId} className="text-xs">
								{formatDateTime(payment.createdAt)}
								{payment.isKeeper && (
									<Tag className="ml-1" color="blue">
										{copy?.keeper ?? "Origjinali"}
									</Tag>
								)}
							</span>
						))}
					</span>
				),
			},
			{
				title: copy?.colOrderPrice ?? "Vlera e porosisë",
				dataIndex: "orderPrice",
				key: "orderPrice",
				align: "right",
				render: (orderPrice: number) => formatMoney(orderPrice),
			},
			{
				title: copy?.colRecordedTotal ?? "Totali i regjistruar",
				key: "recordedTotal",
				align: "right",
				render: (_: unknown, row) => (
					<span className="flex flex-col items-end">
						<span>{formatMoney(row.recordedTotal)}</span>
						<span className="text-xs text-red-600">
							+{formatMoney(Math.abs(row.overpaidDelta))}
						</span>
					</span>
				),
			},
			{
				title: copy?.colDriverCashIn ?? "Barazuar nga Shoferi",
				key: "driverCashIn",
				render: (_: unknown, row) =>
					cashCell(
						cashIds(
							row.payments,
							(p) => p.driverCashRegisterTransactionsInId
						)
					),
			},
			{
				title: copy?.colSellerCash ?? "Barazuar me Shitësin",
				key: "sellerCash",
				render: (_: unknown, row) =>
					cashCell(
						cashIds(
							row.payments,
							(p) => p.sellerCashRegisterTransactionsId
						)
					),
			},
			{
				title: copy?.colDriverCashOut ?? "Paga e Shoferit",
				key: "driverCashOut",
				render: (_: unknown, row) =>
					cashCell(
						cashIds(
							row.payments,
							(p) => p.driverCashRegisterTransactionsOutId
						)
					),
			},
			{
				title: copy?.colActions ?? "Veprimi",
				key: "actions",
				width: 120,
				render: (_: unknown, row) => {
					const button = (
						<Button
							type="link"
							danger
							disabled={!row.canDelete}
							onClick={() => setTarget(row)}
						>
							{copy?.delete ?? "Fshij"}
						</Button>
					);

					// A disabled button with no explanation reads as a bug. The reason code comes
					// from the API so the operator sees WHY finance has to handle this one.
					return row.canDelete ? (
						button
					) : (
						<Tooltip title={blockedReason(row)}>
							<span>{button}</span>
						</Tooltip>
					);
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[copy]
	);

	return (
		<div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={categoryStrings?.label ?? "Pagesa të Dyfishuara"}
				subtitle={
					categoryStrings?.description ??
					"Gjej porositë e paguara dy herë dhe fshij pagesën e dyfishuar"
				}
				backTo="/bulk-order-operations"
				backLabel={common?.back ?? "All categories"}
			/>

			{!categoryConfig && (
				<Alert
					type="error"
					showIcon
					message={
						common?.categoryUnavailable ??
						"This category is not configured for this country."
					}
				/>
			)}

			<Alert
				type="info"
				showIcon
				message={
					copy?.scopeNote ??
					"Fshihet vetëm rreshti i pagesës së dyfishuar. Statusi, historiku dhe arka nuk preken."
				}
			/>

			<div className="rounded-lg border border-gray-200 p-3 flex flex-col gap-3">
				<div className="font-medium">
					{copy?.rangeLabel ?? "Periudha e pagesave"}
				</div>
				<div className="flex flex-wrap items-center gap-3">
					<RangePicker
						value={range}
						format="DD/MM/YYYY"
						allowClear={false}
						disabledDate={(current) =>
							current && current.isAfter(dayjs(), "day")
						}
						onChange={(value) => {
							if (value?.[0] && value?.[1]) {
								changeRange([value[0], value[1]]);
							}
						}}
					/>
					<Button
						type="primary"
						style={rangeTooWide ? undefined : PRIMARY_BUTTON_STYLE}
						loading={loading}
						disabled={rangeTooWide}
						onClick={search}
					>
						{copy?.search ?? "Kërko"}
					</Button>
				</div>
				{rangeTooWide && (
					<Alert
						type="error"
						showIcon
						message={
							copy?.rangeTooWide ??
							`Periudha nuk mund të kalojë ${MAX_DUPLICATE_RANGE_DAYS} ditë.`
						}
					/>
				)}
			</div>

			{searched && (
				<div className="rounded-lg border border-[#4F16C2] bg-[#4F16C2]/5 p-3 flex flex-wrap gap-6 text-sm">
					<span>
						{copy?.summaryOrders ?? "Porosi të prekura"}:{" "}
						<b>{orders.length}</b>
					</span>
					<span>
						{copy?.summaryDeletable ?? "Të fshirshme"}:{" "}
						<b>{deletableCount}</b>
					</span>
					<span>
						{copy?.summaryOverpaid ?? "Totali i mbipaguar"}:{" "}
						<b>{formatMoney(totalOverpaid)}</b>
					</span>
				</div>
			)}

			<Table<DuplicateDeliveryPayment>
				rowKey="orderId"
				size="small"
				columns={columns}
				dataSource={orders}
				loading={loading}
				pagination={false}
				scroll={{ x: true }}
				locale={{
					emptyText: searched
						? copy?.noRows ??
						  "Nuk u gjet asnjë porosi e paguar dy herë"
						: copy?.notSearched ??
						  "Zgjidh periudhën dhe kliko Kërko",
				}}
			/>

			<ConfirmModal
				open={!!target}
				operationLabel={
					categoryStrings?.label ?? "Pagesa të Dyfishuara"
				}
				orderCount={1}
				detail={{
					label: copy?.confirmOrder ?? "Porosia",
					value: target?.displayId ?? String(target?.orderId ?? ""),
				}}
				danger
				dangerWarning={
					copy?.dangerWarning ??
					"Rreshti i pagesës fshihet përgjithmonë dhe nuk mund të rikthehet. Mbetet vetëm pagesa më e vjetër."
				}
				confirmWord={confirmWord}
				onConfirmWordChange={setConfirmWord}
				submitting={deleting}
				onOk={confirmDelete}
				onCancel={closeConfirm}
			/>
		</div>
	);
};

export default DuplicatePayments;
